import React, { useState, useEffect } from "react";
import config from "../../config";
import { tokens } from "../../design-system/tokens";
import Input from "../UiElements/Input";
import Textarea from "../UiElements/Textarea";
import Button from "../UiElements/Button";
import XCloseIcon from "../icons/XCloseIcon";
import styles from "./EditAccidentReportModal.module.css";

export interface AccidentReportEditData {
  _id: string;
  event_driver_name: string;
  event_driver_id: string;
  event_driver_phone: string;
  insured_car_license: string;
  selected_company: string;
  email_to_send_pdf: string;
  insured_damage_description: string;
  acident_description: string;
  acident_date: string;
}

interface EditAccidentReportModalProps {
  report: AccidentReportEditData | null;
  open: boolean;
  onClose: () => void;
  onSaved: (updated: Partial<AccidentReportEditData> & { _id: string }) => void;
}

const fieldLabelStyle = {
  ...tokens.typography.label,
  color: tokens.colors.text.secondary,
};

const modalScrollStyle = {
  ["--scroll-width" as string]: tokens.scroll.width,
  ["--scroll-thumb" as string]: tokens.scroll.thumb,
  ["--scroll-thumb-hover" as string]: tokens.scroll.thumbHover,
  ["--scroll-track" as string]: tokens.scroll.track,
  ["--scroll-thumb-radius" as string]: tokens.borderRadius.full,
};

const EditAccidentReportModal: React.FC<EditAccidentReportModalProps> = ({
  report,
  open,
  onClose,
  onSaved,
}) => {
  const [form, setForm] = useState<Partial<AccidentReportEditData>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (report) {
      setForm({
        event_driver_name: report.event_driver_name || "",
        event_driver_id: report.event_driver_id || "",
        event_driver_phone: report.event_driver_phone || "",
        insured_car_license: report.insured_car_license || "",
        selected_company: report.selected_company || "",
        email_to_send_pdf: report.email_to_send_pdf || "",
        insured_damage_description: report.insured_damage_description || "",
        acident_description: report.acident_description || "",
        acident_date: report.acident_date || "",
      });
      setError("");
    }
  }, [report]);

  if (!open) return null;

  const handleChange = (field: keyof AccidentReportEditData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!report?._id) return;
    setSaving(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${config.backendUrl}/forms/accident-report/${report._id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(form),
        }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "שגיאה בעדכון");
      }
      const updated = await res.json();
      onSaved(updated);
      onClose();
    } catch (err: any) {
      setError(err?.message || "שגיאה בעדכון הדוח");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={styles.overlay}
      style={{ background: "rgba(29, 45, 80, 0.45)" }}
      onClick={onClose}
    >
      <div
        className={styles.modal}
        style={{
          background: tokens.colors.background,
          borderRadius: tokens.borderRadius.xl,
          boxShadow: tokens.shadows.xl,
          ...modalScrollStyle,
        }}
        onClick={(e) => e.stopPropagation()}
        dir="rtl"
      >
        <div
          className={styles.header}
          style={{ borderBottom: `1px solid ${tokens.colors.border}` }}
        >
          <h2 style={{ ...tokens.typography.h4, color: tokens.colors.text.primary }}>
            עריכת דוח תאונה
          </h2>
          <button
            type="button"
            className={styles.closeBtn + " ui-elements-button"}
            onClick={onClose}
            aria-label="סגור"
            style={{ color: tokens.colors.text.secondary }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = tokens.colors.surface;
              e.currentTarget.style.color = tokens.colors.text.primary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = tokens.colors.text.secondary;
            }}
          >
            <XCloseIcon width={20} height={20} style={{ display: "block" }} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.row}>
            <Input
              label="שם הנהג"
              type="text"
              value={form.event_driver_name ?? ""}
              onChange={handleChange("event_driver_name")}
              rtl
              width="100%"
            />
          </div>
          <div className={styles.row}>
            <Input
              label="תעודת זהות נהג"
              type="text"
              value={form.event_driver_id ?? ""}
              onChange={handleChange("event_driver_id")}
              rtl
              width="100%"
            />
          </div>
          <div className={styles.row}>
            <Input
              label="טלפון נהג"
              type="text"
              value={form.event_driver_phone ?? ""}
              onChange={handleChange("event_driver_phone")}
              rtl
              width="100%"
            />
          </div>
          <div className={styles.row}>
            <Input
              label="מספר רכב מבוטח"
              type="text"
              value={form.insured_car_license ?? ""}
              onChange={handleChange("insured_car_license")}
              rtl
              width="100%"
            />
          </div>
          <div className={styles.row}>
            <Input
              label="חברה נבחרת"
              type="text"
              value={form.selected_company ?? ""}
              onChange={handleChange("selected_company")}
              rtl
              width="100%"
            />
          </div>
          <div className={styles.row}>
            <Input
              label="אימייל"
              type="text"
              value={form.email_to_send_pdf ?? ""}
              onChange={handleChange("email_to_send_pdf")}
              rtl
              width="100%"
            />
          </div>
          <div className={styles.row}>
            <Input
              label="תאריך תאונה"
              type="text"
              value={form.acident_date ?? ""}
              onChange={handleChange("acident_date")}
              placeholder="DD/MM/YYYY"
              rtl
              width="100%"
            />
          </div>
          <div className={styles.row}>
            <label className={styles.fieldLabel} style={fieldLabelStyle}>
              תיאור הנזק
            </label>
            <Textarea
              value={form.insured_damage_description ?? ""}
              onChange={handleChange("insured_damage_description")}
              rows={3}
              rtl
              width="100%"
            />
          </div>
          <div className={styles.row}>
            <label className={styles.fieldLabel} style={fieldLabelStyle}>
              תיאור המקרה
            </label>
            <Textarea
              value={form.acident_description ?? ""}
              onChange={handleChange("acident_description")}
              rows={3}
              rtl
              width="100%"
            />
          </div>
          {error && (
            <div
              className={styles.error}
              style={{
                ...tokens.typography.bodySmall,
                color: tokens.colors.error,
              }}
            >
              {error}
            </div>
          )}
          <div className={styles.actions}>
            <Button
              label="ביטול"
              onClick={onClose}
              color="outlineSecondary"
              size="large"
            />
            <Button
              label={saving ? "שומר..." : "שמור"}
              buttonType="submit"
              color="primary"
              size="large"
              disabled={saving}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditAccidentReportModal;
